import React, { useContext, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { FetchContext } from "../../context/FetchContext";
import { InitiateGcsResumable } from "../../storage/Upload";
import {
    SUPPORTED_AUDIO_EXTENSIONS,
    SUPPORTED_VIDEO_EXTENSIONS,
    validateFileForUpload,
} from "../../utils/fileValidation";
import { LibrettoProjectTypes, uploadFileWithProgress } from "../../utils/utils";

const MAX_MOBILE_UPLOAD_SIZE = 250 * 1024 * 1024;

const formatFileSize = (bytes: number) => {
    const megabytes = bytes / (1024 * 1024);
    return megabytes >= 1 ? `${megabytes.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const fileTypeFor = (file: File): "audio" | "video" | null => {
    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    if (SUPPORTED_AUDIO_EXTENSIONS.includes(extension)) return "audio";
    if (SUPPORTED_VIDEO_EXTENSIONS.includes(extension) || extension === "avi") return "video";
    return null;
};

const MobileUploadPage: React.FC = () => {
    const { projectId = "" } = useParams();
    const authContext = useContext(AuthContext);
    const fetchContext = useContext(FetchContext);
    const [file, setFile] = useState<File | null>(null);
    const [fileType, setFileType] = useState<"audio" | "video" | null>(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [complete, setComplete] = useState(false);
    const [error, setError] = useState("");

    const selectFile = async (selectedFile?: File) => {
        setError("");
        setFile(null);
        setFileType(null);

        if (!selectedFile) return;
        if (selectedFile.size > MAX_MOBILE_UPLOAD_SIZE) {
            setError("This file is larger than the 250 MB mobile upload limit.");
            return;
        }

        const type = fileTypeFor(selectedFile);
        const validation = await validateFileForUpload(selectedFile);
        if (!type || validation.mode === "rejected") {
            setError(validation.reason || "Choose a supported audio or video file.");
            return;
        }

        setFile(selectedFile);
        setFileType(type);
    };

    const upload = async () => {
        if (!file || !fileType || !projectId) return;
        setUploading(true);
        setProgress(0);
        setError("");

        try {
            const { data } = await fetchContext.authAxios.post(
                "/assets/upload",
                {
                    projectId,
                    workspaceId: authContext.getCurrentWorkspaceId?.(),
                    projectType: LibrettoProjectTypes.ProjectTypeEditor,
                    isRecording: false,
                    isScreenRecording: false,
                    title: file.name,
                    fileType,
                    languageCode: "auto",
                    serverSideFirst: true,
                },
                { headers: { Authorization: `Bearer ${authContext.getToken()}` } },
            );

            const sessionUrl = await InitiateGcsResumable(data.signedUrl, file, file.type);
            await uploadFileWithProgress(
                sessionUrl,
                file,
                (value: number) => setProgress(Math.round(value)),
                setComplete,
            );
        } catch (uploadError: any) {
            setError(uploadError?.message || "Upload failed. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100dvh",
                px: 2.5,
                pt: "calc(env(safe-area-inset-top) + 28px)",
                pb: "calc(env(safe-area-inset-bottom) + 28px)",
                backgroundColor: "background.default",
            }}
        >
            <Box sx={{ maxWidth: 440, mx: "auto" }}>
                <Box
                    component="img"
                    src="/v2-logo.svg"
                    alt="Libretto"
                    sx={{ display: "block", width: 102, height: "auto", mb: 3 }}
                />

                <Paper
                    elevation={0}
                    sx={{
                        border: "1px solid",
                        borderColor: "var(--app-border-subtle)",
                        borderRadius: 4,
                        px: { xs: 2.25, sm: 3 },
                        py: { xs: 2.5, sm: 3 },
                        boxShadow: "0 18px 50px rgba(35, 55, 90, 0.08)",
                    }}
                >
                    {complete ? (
                        <Box sx={{ textAlign: "center", py: 2 }}>
                            <CheckCircleRounded sx={{ fontSize: 58, color: "#20A464", mb: 1.5 }} />
                            <Typography component="h1" sx={{ fontSize: 25, lineHeight: 1.2, fontWeight: 750, letterSpacing: "-0.5px" }}>
                                Upload complete
                            </Typography>
                            <Typography sx={{ color: "text.secondary", fontSize: 14, lineHeight: 1.55, mt: 1.25 }}>
                                Your file is being prepared in Libretto. You can close this page and find it in your project&apos;s Files.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography component="h1" sx={{ fontSize: 25, lineHeight: 1.2, fontWeight: 750, letterSpacing: "-0.5px" }}>
                                Upload from mobile
                            </Typography>
                            <Typography sx={{ color: "text.secondary", fontSize: 14, lineHeight: 1.55, mt: 1, mb: 2.5 }}>
                                Add an audio or video file directly to the Libretto project open on your computer.
                            </Typography>

                            <Button
                                component="label"
                                variant="outlined"
                                fullWidth
                                disabled={uploading}
                                sx={{
                                    minHeight: 128,
                                    px: 2,
                                    borderRadius: 3,
                                    borderStyle: "dashed",
                                    borderWidth: "1.5px",
                                    borderColor: file ? "primary.main" : "var(--app-border-muted)",
                                    backgroundColor: file ? "rgba(43, 107, 253, 0.045)" : "var(--app-bg-subtle)",
                                    color: "text.primary",
                                    textAlign: "left",
                                    "&:hover": {
                                        borderWidth: "1.5px",
                                        borderColor: "primary.main",
                                        backgroundColor: "rgba(43, 107, 253, 0.06)",
                                    },
                                }}
                            >
                                <Box sx={{ width: "100%", minWidth: 0, textAlign: "center" }}>
                                    <CloudUploadOutlined sx={{ fontSize: 31, color: "primary.main", mb: 0.75 }} />
                                    <Typography sx={{ fontSize: 14.5, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {file ? file.name : "Choose audio or video"}
                                    </Typography>
                                    <Typography sx={{ color: "text.secondary", fontSize: 12, mt: 0.5 }}>
                                        {file
                                            ? `${fileType === "video" ? "Video" : "Audio"} · ${formatFileSize(file.size)} · Tap to change`
                                            : "Audio or video · up to 250 MB"}
                                    </Typography>
                                </Box>
                                <input
                                    hidden
                                    type="file"
                                    accept="audio/*,video/*,.avi"
                                    onChange={(event) => void selectFile(event.target.files?.[0])}
                                />
                            </Button>

                            {error && (
                                <Alert severity="error" sx={{ mt: 2, borderRadius: 2.5, fontSize: 13 }}>
                                    {error}
                                </Alert>
                            )}

                            {uploading && (
                                <Box sx={{ mt: 2.25 }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.85 }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 650 }}>Uploading</Typography>
                                        <Typography sx={{ color: "text.secondary", fontSize: 12.5 }}>{progress}%</Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={progress}
                                        sx={{ height: 8, borderRadius: 10, backgroundColor: "var(--app-bg-subtle)", "& .MuiLinearProgress-bar": { borderRadius: 10 } }}
                                    />
                                    <Typography sx={{ color: "text.secondary", fontSize: 12, mt: 1 }}>
                                        Keep this page open until the upload finishes.
                                    </Typography>
                                </Box>
                            )}

                            {file && (
                                <Button
                                    variant="contained"
                                    fullWidth
                                    disableElevation
                                    disabled={uploading}
                                    onClick={upload}
                                    sx={{ mt: 2.25, minHeight: 50, borderRadius: 2.5, fontSize: 15, fontWeight: 700 }}
                                >
                                    {uploading ? `Uploading… ${progress}%` : "Upload to project"}
                                </Button>
                            )}

                        </>
                    )}
                </Paper>
            </Box>
        </Box>
    );
};

export default MobileUploadPage;
